import { useQuery } from '@apollo/client'
import { sortBy } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { BackToOutletListLink } from '@src/components/BackToOutletListLink/BackToOutletListLink'
import { getOutletSearchBySegmentVariables } from '@src/components/BusinessSegments/getSegmentOutletSearchVariables'
import { getBusinessSegmentDocument } from '@src/components/BusinessSegments/queries/__generated__/getBusinessSegment.graphql-interface'
import { getBusinessSegmentOutletsDocument } from '@src/components/BusinessSegments/queries/__generated__/getBusinessSegmentOutlets.graphql-interface'
import { ErrorPage } from '@src/components/Errors/ErrorPage'
import { Description } from '@src/components/FeaturedOutlets/FeaturedOutlets.styles'
import { TextClamp } from '@src/components/Text/Clamp'
import { useCategories } from '@src/hooks/useCategories'
import { useFulfilmentFilter } from '@src/hooks/useFulfilmentFilter/useFulfilmentFilter'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { ALL_FULFILMENT_METHODS_SORTED } from '@src/utils/fulfilment'

import { CategoryFilteredOutlets } from '../OutletList/CategoryFilteredOutlets'
import { OutletCards } from '../OutletList/OutletCards'
import {
  HeaderContainer,
  OutletsOuterContainer,
} from '../OutletList/OutletList.styles'
import {
  LocationType,
  NarrowFulfilmentMethodInputType,
  OrderFulfillmentMethods,
  Outlet,
  SegmentOrderBy,
} from '@src/graphql-types'
import {
  DateifyOutlet,
  parseOutletDates,
} from '@src/utils/fulfilmentTimes/parsers'
import { OutletListCompactSkeleton } from '../OutletList/OutletListCompactSkeleton'
import { OrderabilityFragment } from '../../fragments/__generated__/OrderabilityFragment'

export const SegmentOutletList: React.FC = () => {
  const fulfilmentFilter = useFulfilmentFilter()
  const marketplace = useMarketplace()
  const { segmentId } = useParams<{ segmentId: string }>()

  const { t } = useTranslation(['copy'])

  const { selectedCategories } = useCategories()

  const orderOutlets = <
    T extends DateifyOutlet<
      Pick<
        Outlet,
        | 'displayName'
        | 'availableFulfilmentInputMethods'
        | 'isOnline'
        | 'isOpen'
        | 'promoteOutlet'
        | 'nextOpenDate'
      > & {
        orderability: OrderabilityFragment
      }
    >
  >(
    outlets: T[],
    orderBy: SegmentOrderBy | null
  ): T[] => {
    if (!orderBy) return outlets

    if (orderBy === SegmentOrderBy.ALPHABETICAL) {
      return sortBy(outlets, 'displayName')
    }

    return outlets
  }

  // gotta use state cause its not detecting selected date change
  const variables = segmentId
    ? getOutletSearchBySegmentVariables({
        fulfilmentFilterData: fulfilmentFilter.data,
        marketplaceId: marketplace.id,
        businessSegmentId: segmentId,
        limitOutlets: false,
      })
    : undefined

  const allOutletsForSegmentData = useQuery(getBusinessSegmentOutletsDocument, {
    variables: segmentId
      ? {
          fulfilmentMethod: NarrowFulfilmentMethodInputType.DELIVERY,
          input: {
            locationType: LocationType.EVERYWHERE,
            businessSegmentId: segmentId,
            marketplaceId: marketplace.id,
            fulfilmentMethods: [
              OrderFulfillmentMethods.DELIVERY,
              OrderFulfillmentMethods.COLLECTION,
              OrderFulfillmentMethods.TABLE,
            ],
          },
        }
      : undefined,
  })

  const { data, loading, error } = useQuery(
    getBusinessSegmentOutletsDocument,
    variables ? { variables } : { skip: true }
  )

  const segmentResponse = useQuery(getBusinessSegmentDocument, {
    variables: segmentId ? { id: segmentId } : undefined,
  })
  const businessSegment = segmentResponse.data?.businessSegment
  const isFulfilmentFiltered = businessSegment?.filterSegment

  const allOutletsForSegment =
    allOutletsForSegmentData.data?.businessSegmentOutlets.map(
      ({ nextOpenDate, ...outlet }) =>
        parseOutletDates({
          nextOpenDate: new Date(nextOpenDate).toISOString(),
          ...outlet,
        })
    )
  const sortedAllOutletsForSegment =
    allOutletsForSegment?.length && businessSegment?.orderBy
      ? orderOutlets(allOutletsForSegment, businessSegment.orderBy)
      : allOutletsForSegment

  if (loading) {
    return <OutletListCompactSkeleton />
  }
  if (error) {
    return <ErrorPage logError={error} />
  }
  if (!data || !businessSegment) {
    return <ErrorPage />
  }

  const outlets = data.businessSegmentOutlets.map(
    ({ nextOpenDate, ...outlet }) =>
      parseOutletDates({
        nextOpenDate: new Date(nextOpenDate).toISOString(),
        ...outlet,
      })
  )

  const sortedOutlets = orderOutlets(outlets, businessSegment?.orderBy)

  const outletsToRender = !isFulfilmentFiltered
    ? sortedAllOutletsForSegment || []
    : sortedOutlets

  const fulfilmentsTitle =
    fulfilmentFilter.data.nonEmptyFulfilmentMethods.length ===
      ALL_FULFILMENT_METHODS_SORTED.length || !isFulfilmentFiltered
      ? marketplace.allOutletsText || t('all')
      : fulfilmentFilter.data.nonEmptyFulfilmentMethods
          .map(fulfilment => t(fulfilment.toLowerCase()))
          .join(' and ')

  return selectedCategories.length ? (
    <CategoryFilteredOutlets outlets={outletsToRender} />
  ) : (
    <OutletsOuterContainer>
      <BackToOutletListLink showContainer />
      {outletsToRender.length > 0 && (
        <HeaderContainer>
          {` ${businessSegment.name ?? ''}
            ${` - `}${fulfilmentsTitle}`}
        </HeaderContainer>
      )}
      {!!businessSegment.description?.length && outletsToRender.length > 0 && (
        <Description>
          <TextClamp clamp={5}>{businessSegment.description}</TextClamp>
        </Description>
      )}
      <OutletCards outlets={outletsToRender} />
    </OutletsOuterContainer>
  )
}
